import { PermsContextType } from 'contexts/perms.types';
import React from 'react';
import { FaChevronDown, FaChevronUp, FaLock } from 'react-icons/fa6';

interface Props {
  roleId: number | string;
  perms: PermsContextType;
  hierarchy: Record<string, number>;
  yourHighestRoleHierarchyId?: number | null;
  onMoveUp?: (roleId: number | string) => void;
  onMoveDown?: (roleId: number | string) => void;
  /** True while a column sort orders the rows: a chevron swaps hierarchy neighbours, not rows. */
  isRankLocked?: boolean;
}

const RANK_LOCKED_REASON = 'Clear the column sort to change the rank';

export default function RoleHierarchyCell({
  roleId,
  perms,
  hierarchy,
  yourHighestRoleHierarchyId,
  onMoveUp,
  onMoveDown,
  isRankLocked = false,
}: Props) {
  const rank = hierarchy[String(roleId)];

  const roleIdNum = Number(roleId);
  const accessLevelNum = Number(perms.accessLevel ?? -1);

  const canEdit = (roleIdNum >= 0 && accessLevelNum >= 12) || accessLevelNum === 8;
  // aria-disabled (not disabled) keeps the chevron focusable, so the title reaches assistive tech.
  const chevronClassName = `btn btn-link p-0 border-0 lh-1 text-reset${isRankLocked ? ' opacity-50' : ''}`;

  return (
    <div className="d-flex flex-row gap-1 justify-content-start align-items-center text-white">
      {canEdit ? (
        <div
          style={{
            width: 50,
          }}
        >
          <div className="d-flex flex-column align-items-center justify-content-center">
            {rank > 0 && (
              <button
                type="button"
                className={chevronClassName}
                aria-label="Move up"
                aria-disabled={isRankLocked}
                title={isRankLocked ? RANK_LOCKED_REASON : undefined}
                onClick={() => {
                  if (!isRankLocked) onMoveUp?.(roleId);
                }}
              >
                <FaChevronUp aria-hidden="true" />
              </button>
            )}
            {rank >= 0 ? <div>{(rank ?? 0) + 1}</div> : <div />}
            {rank < Object.keys(hierarchy).length - 1 && (
              <button
                type="button"
                className={chevronClassName}
                aria-label="Move down"
                aria-disabled={isRankLocked}
                title={isRankLocked ? RANK_LOCKED_REASON : undefined}
                onClick={() => {
                  if (!isRankLocked) onMoveDown?.(roleId);
                }}
              >
                <FaChevronDown aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <div
          className="d-flex justify-content-center opacity-50"
          style={{
            width: 50,
          }}
        >
          {accessLevelNum === 10 &&
          roleIdNum !== -2 &&
          (hierarchy[String(roleId)] ?? -1) > (yourHighestRoleHierarchyId ?? -1) ? (
            <div />
          ) : (
            <FaLock />
          )}
        </div>
      )}
    </div>
  );
}
